import axios from 'axios';
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { usePermissions } from '@/lib/PermissionsContext';
import { formatLocalDate } from '@/utils/formatLocalTime'; // 🆕 [P1 ext]
import { GoogleMapsBtn } from '@/components/GoogleMapsBtn';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft, Building2, MapPin, Calendar, User, FileText, DollarSign,
  ClipboardList, MessageSquare, Clock, BarChart3, Edit, Camera, Calculator, Shield, FileDown, CheckCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { OngletSoumissions } from '@/components/crm/OngletSoumissions';
import { OngletCalculatrice } from '@/components/crm/OngletCalculatrice';
import { OngletBonTravail } from '@/components/crm/OngletBonTravail';
import { OngletFactures } from '@/components/crm/OngletFactures';
import { OngletNotes } from '@/components/crm/OngletNotes';
import { OngletStatistiques } from '@/components/crm/OngletStatistiques';
import { OngletHistorique } from '@/components/crm/OngletHistorique';
import { OngletPhotos } from '@/components/crm/OngletPhotos';
import { STATUT_COLORS, STATUT_LABELS, RAISONS_ABANDON } from '@/lib/statutsConfig';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

export function DetailsProjet() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, user } = useAuth();
  const { canSeeDossierTab, canDoBonTravailAction, canDo } = usePermissions() || {};
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(true);
  // 🎯 [Phase v13.49 — 2026-07-20] Montants canoniques du dossier (source unique de vérité).
  // Recharge automatique à chaque modification du dossier via `refreshMontants()`.
  const [montantsDossier, setMontantsDossier] = useState(null);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'info');
  const [selectedBonId, setSelectedBonId] = useState(searchParams.get('bonId') || null);
  const autoOpenCommande = searchParams.get('scrollToCommande') === '1';
  const hideAddress = searchParams.get('hide_address') === '1';
  const [showModifier, setShowModifier] = useState(false);
  const [showDechargeDialog, setShowDechargeDialog] = useState(false);
  const [showFinaliserDialog, setShowFinaliserDialog] = useState(false);
  const [finaliserDates, setFinaliserDates] = useState({ debut: '', fin: '' });
  const [finaliserLoading, setFinaliserLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [editForm, setEditForm] = useState({});
  const [openIncidentsCount, setOpenIncidentsCount] = useState(0);
  const [activeBonsCount, setActiveBonsCount] = useState(0);
  // 🆕 2026-05-01 — Dialogs Inactif
  const [showInactifDialog, setShowInactifDialog] = useState(false);
  const [inactifForm, setInactifForm] = useState({ raison: '', raison_autre: '' });
  const [inactifLoading, setInactifLoading] = useState(false);
  const [showReactiverDialog, setShowReactiverDialog] = useState(false);
  const [reactiverCible, setReactiverCible] = useState('prospect');
  const [reactiverLoading, setReactiverLoading] = useState(false);

  const fetchDossier = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/crm/dossiers/${id}`);
      if (response.status !== 200) throw new Error('Dossier non trouvé');
      const data = response.data;
      setDossier(data);
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Dossier non trouvé');
      navigate('/crm/dossiers');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  // 🎯 [Phase v13.49 — 2026-07-20] Fetch des montants canoniques via source unique
  const fetchMontantsDossier = useCallback(async () => {
    if (!id) return;
    try {
      const r = await axios.get(`${API_URL}/api/crm/dossiers/${id}/montants`);
      setMontantsDossier(r.data);
    } catch (e) {
      // 403 = terrain (pas visible), 404 = dossier introuvable → silencieux (fallback affichage)
      if (e?.response?.status && e.response.status >= 500) {
        console.error('[v13.49] Erreur chargement montants dossier :', e);
      }
      setMontantsDossier(null);
    }
  }, [id]);

  useEffect(() => { fetchDossier(); }, [fetchDossier]);
  useEffect(() => { fetchMontantsDossier(); }, [fetchMontantsDossier]);

  // Rediriger vers le premier onglet accessible si le tab par défaut n'est pas autorisé
  useEffect(() => {
    if (!canSeeDossierTab || !activeTab) return;
    if (!canSeeDossierTab(activeTab === 'bon-travail' ? 'bon_travail' : activeTab)) {
      setActiveTab('info');
    }
  }, [canSeeDossierTab]);
  useEffect(() => {
    if (!id || !token) return;
    axios.get(`${API_URL}/api/incidents?dossier_id=${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.status === 200 ? r.data : { incidents: [] })
      .then(data => {
        const open = (data.incidents || []).filter(i => !i.resolved).length;
        setOpenIncidentsCount(open);
      })
      .catch(() => {});
  }, [id, token]);

  // Charger le nombre de bons de travail actifs (non terminés)
  useEffect(() => {
    if (!id) return;
    axios.get(`${API_URL}/api/crm/bons-travail?dossier_id=${id}`)
      .then(r => r.status === 200 ? r.data : { bons_travail: [] })
      .then(data => {
        const actifs = (data.bons_travail || []).filter(b => !b.termine).length;
        setActiveBonsCount(actifs);
      })
      .catch(() => {});
  }, [id]);

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/employees`);
      if (res.status === 200) {
        const data = res.data;
        setEmployees(data.employees || []);
      }
    } catch (e) { console.error(e); }
  };

  const openModifier = () => {
    setEditForm({
      nom_dossier: dossier.nom_dossier || '',
      departement: dossier.departement || 'PRQC',
      type_contrat: dossier.type_contrat || 'peinture',
      adresse_travaux: dossier.adresse_travaux || '',
      ville_travaux: dossier.ville_travaux || '',
      code_postal_travaux: dossier.code_postal_travaux || '',
      representant_id: dossier.representant_id || '',
      representant_nom: dossier.representant_nom || '',
      chef_equipe_id: dossier.chef_equipe_id || '',
      chef_equipe_nom: dossier.chef_equipe_nom || '',
      charge_projet_id: dossier.charge_projet_id || '',
      charge_projet_nom: dossier.charge_projet_nom || '',
      date_production: dossier.date_production || '',
      date_creation: dossier.date_creation ? dossier.date_creation.split('T')[0] : '',
      type_travaux: dossier.type_travaux || '',
      categorie_travaux: dossier.categorie_travaux || '',
    });
    fetchEmployees();
    setShowModifier(true);
  };

  const handleRepChange = (empId) => {
    if (empId === 'aucun') {
      setEditForm(prev => ({ ...prev, representant_id: '', representant_nom: '' }));
    } else {
      const emp = employees.find(e => e.id === empId);
      if (emp) setEditForm(prev => ({ ...prev, representant_id: empId, representant_nom: `${emp.prenom} ${emp.nom}` }));
    }
  };

  const handleChefChange = (empId) => {
    if (empId === 'aucun') {
      setEditForm(prev => ({ ...prev, chef_equipe_id: '', chef_equipe_nom: '' }));
    } else {
      const emp = employees.find(e => e.id === empId);
      if (emp) setEditForm(prev => ({ ...prev, chef_equipe_id: empId, chef_equipe_nom: `${emp.prenom} ${emp.nom}` }));
    }
  };

  const handleChargeChange = (empId) => {
    if (empId === 'aucun') {
      setEditForm(prev => ({ ...prev, charge_projet_id: '', charge_projet_nom: '' }));
    } else {
      const emp = employees.find(e => e.id === empId);
      if (emp) setEditForm(prev => ({ ...prev, charge_projet_id: empId, charge_projet_nom: `${emp.prenom} ${emp.nom}` }));
    }
  };

  const chefsEquipe = employees.filter(e => e.type_employe === 'terrain' && e.role_terrain === 'chef_equipe');
  const representants = employees.filter(e => e.type_employe === 'representant');
  const chargesProjets = employees.filter(e => e.type_employe === 'admin' && e.role_admin === 'charge_projet');

  const handleSaveModifier = async () => {
    try {
      const formDate = editForm.date_creation;
      const dateCreationISO = formDate && !formDate.includes('T')
        ? new Date(formDate + 'T12:00:00').toISOString()
        : formDate;
      const payload = { ...dossier, ...editForm, date_creation: dateCreationISO };
      const res = await axios.put(`${API_URL}/api/crm/dossiers/${dossier.id}`, payload);
      if (res.status !== 200) throw new Error('Erreur');
      toast.success('Projet modifié!');
      setShowModifier(false);
      fetchDossier();
    } catch (e) {
      toast.error('Erreur lors de la modification');
    }
  };

  const togglePhotosRequises = async () => {
    const hasPhotos = (dossier.photos || []).length > 0;
    const newVal = !dossier.photos_requises;
    try {
      await axios.patch(`${API_URL}/api/crm/dossiers/${dossier.id}/toggle-photos-requises`, {
        photos_requises: newVal
      });
      if (newVal) {
        toast.success('📷 Section photos activée — Photos requises pour ce projet');
        setActiveTab('photos');
      } else if (hasPhotos) {
        // Il y a encore des photos → onglet reste visible, pas besoin de changer l'onglet actif
        toast.success('Photos marquées comme non-requises (les photos restent visibles)');
      } else {
        // Aucune photo + désactivé → quitter l'onglet photos
        toast.success('Section photos désactivée');
        if (activeTab === 'photos') setActiveTab('bon-travail');
      }
      fetchDossier();
    } catch {
      toast.error('Erreur');
    }
  };

  if (loading || !dossier) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  // L'onglet Photos est visible si photos requises OU s'il y a déjà des photos uploadées
  const showPhotosTab = dossier.photos_requises || (dossier.photos || []).length > 0;
  const nbPhotos = (dossier.photos || []).length;

  return (
    <div className="p-3 sm:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 no-print">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Retour
        </Button>

        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{dossier.nom_dossier}</h1>
              <Badge className={STATUT_COLORS[dossier.statut] || 'bg-gray-100'}>
                {STATUT_LABELS[dossier.statut] || dossier.statut}
              </Badge>
              <Badge variant="outline">{dossier.departement}</Badge>
            </div>
            <p className="text-gray-600">{dossier.numero_dossier}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {(!canDoBonTravailAction || canDoBonTravailAction('bon_ceduler_rdv')) && (
            <Button
              onClick={() => navigate(`/crm/calendrier-rendez-vous?dossier_id=${dossier.id}&client_id=${dossier.client_id}`)}
              className="bg-green-600 hover:bg-green-700"
              title="Céduler une Soumission"
            >
              <Calendar className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Céduler</span>
            </Button>
            )}
            {/* 🆕 [Phase v13.61 Vague 5b — 2026-08-25] Branchement RBAC via `dossier_toggle_photos_requises`.
                Bascule Owner-only pour activer/désactiver la section photos marketing d'un dossier. */}
            {canDo && canDo('dossier_toggle_photos_requises') && (
            <Button
              onClick={togglePhotosRequises}
              variant={dossier.photos_requises ? "default" : "outline"}
              title={dossier.photos_requises ? "Photos requises ✅ — Cliquez pour retirer l'obligation" : "Cliquez pour activer la section photos"}
              className={dossier.photos_requises ? "bg-blue-600 hover:bg-blue-700" : ""}
            >
              <Camera className="w-4 h-4" />
              {(dossier.photos || []).length > 0 && (
                <span className="ml-1 text-xs">{(dossier.photos || []).length}</span>
              )}
            </Button>
            )}
            {dossier.statut === 'suivi_a_faire' && (
              <Button
                onClick={async () => {
                  try {
                    await axios.patch(`${API_URL}/api/crm/dossiers/${dossier.id}/marquer-suivi-fait`);
                    toast.success('Suivi marqué comme fait!');
                    fetchDossier();
                  } catch (e) { toast.error('Erreur'); }
                }}
                variant="outline" className="border-lime-600 text-lime-700 text-xs"
              >
                <span className="hidden sm:inline">Marquer </span>Suivi Fait
              </Button>
            )}
            {/* 🆕 2026-05-01 — Marquer Inactif (prospect ou soum_a_faire uniquement) */}
            {['prospect', 'soum_a_faire'].includes(dossier.statut) && (
              <Button
                onClick={() => { setInactifForm({ raison: '', raison_autre: '' }); setShowInactifDialog(true); }}
                variant="outline"
                className="border-slate-500 text-slate-700 text-xs"
                data-testid="btn-marquer-inactif"
              >
                <span className="hidden sm:inline">Marquer </span>Inactif
              </Button>
            )}
            {/* 🆕 2026-05-01 — Réactiver (si abandonne) */}
            {dossier.statut === 'abandonne' && (
              <Button
                onClick={() => { setReactiverCible('prospect'); setShowReactiverDialog(true); }}
                variant="outline"
                className="border-emerald-600 text-emerald-700 text-xs"
                data-testid="btn-reactiver-dossier"
              >
                <span className="hidden sm:inline">Réactiver </span>Dossier
              </Button>
            )}
            {/* 🆕 [Phase v13.61 Vague 5b — 2026-08-25] Branchement RBAC via `dossier_finaliser`.
                Owner-only pour finaliser un dossier (fige les dates de production).
                🔒 Cat H aussi : le statut du dossier restreint dynamiquement le bouton (`!['finalise', 'ferme'].includes`). */}
            {canDo && canDo('dossier_finaliser') && !['finalise', 'ferme'].includes(dossier.statut) && (
              <Button
                onClick={() => {
                  setFinaliserDates({ debut: '', fin: '' });
                  setShowFinaliserDialog(true);
                }}
                variant="outline" className="border-emerald-600 text-emerald-700 text-xs"
                data-testid="btn-finaliser-dossier"
              >
                <CheckCircle className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Finaliser</span>
              </Button>
            )}
            {(!canDoBonTravailAction || canDoBonTravailAction('bon_decharge')) && (
            <Button
              variant="outline"
              className="border-purple-600 text-purple-700 hover:bg-purple-50"
              onClick={() => setShowDechargeDialog(true)}
              data-testid="decharge-legale-btn"
              title="Décharge Légale"
            >
              <Shield className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Décharge</span>
            </Button>
            )}
            {(!canDoBonTravailAction || canDoBonTravailAction('bon_pdf')) && (
            <Button
              variant="outline"
              className="border-gray-400 text-gray-700 hover:bg-gray-50"
              data-testid="rapport-pdf-btn"
              title="Rapport PDF"
              onClick={async () => {
                try {
                  toast.info('Génération du rapport PDF en cours...');
                  const res = await axios.get(`${API_URL}/api/crm/dossiers/${dossier.id}/rapport-pdf`, {
                    responseType: 'blob'
                  });
                  if (res.status !== 200) throw new Error('Erreur génération PDF');
                  const blob = res.data;
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `rapport_${dossier.nom_dossier?.replace(/\s+/g,'_') || 'projet'}.pdf`;
                  a.click();
                  URL.revokeObjectURL(url);
                  toast.success('Rapport PDF téléchargé !');
                } catch (e) {
                  toast.error('Erreur lors de la génération du PDF');
                }
              }}
            >
              <FileDown className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">PDF</span>
            </Button>
            )}
            {(!canDoBonTravailAction || canDoBonTravailAction('bon_modifier_dossier')) && (
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={openModifier}>
              <Edit className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline" translate="no">Modifier</span>
            </Button>
            )}
          </div>
        </div>
      </div>

      {/* Résumé */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4 no-print">
        <Card className="col-span-1">
          <CardContent className="pt-3 pb-3">
            <div className="flex items-center gap-2">
              <Building2 className="w-6 h-6 text-blue-600 shrink-0" />
              <div className="min-w-0">
                <p className="text-xs text-gray-500">Client</p>
                <p className="font-semibold text-sm truncate">{dossier.client_nom}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        {(!canDoBonTravailAction || canDoBonTravailAction('bon_voir_montant')) && (
        <Card className="col-span-1">
          <CardContent className="pt-3 pb-3">
            <div className="flex items-center gap-2">
              <DollarSign className="w-6 h-6 text-emerald-600 shrink-0" />
              <div className="min-w-0">
                <p className="text-xs text-gray-500">Montant (sans taxes)</p>
                <p className="font-semibold text-sm truncate" data-testid="dossier-header-montant-ht-total">
                  {(() => {
                    // 🎯 [Phase v13.49 — 2026-07-20] Source unique de vérité (bug C fix) :
                    // Utilise `montant_total_ht` du helper backend (inclut soumission +
                    // ajouts + complémentaires + cash). Rétrocompat : fallback ancien
                    // calcul si l'endpoint n'a pas encore répondu ou si guard 403.
                    if (montantsDossier && typeof montantsDossier.montant_total_ht === 'number' && montantsDossier.montant_total_ht > 0) {
                      return montantsDossier.montant_total_ht.toLocaleString('fr-CA', {
                        style: 'currency', currency: 'CAD',
                      });
                    }
                    // Fallback rétrocompat (endpoint pas encore migré / non chargé)
                    let montantTTC = parseFloat(dossier.montant_soumission || dossier.montant_final || 0);
                    if (montantTTC <= 0) {
                      const soumAcceptee = (dossier.soumissions || []).find(s => s.statut === 'acceptee');
                      if (soumAcceptee) montantTTC = parseFloat(soumAcceptee.montant_final || soumAcceptee.montant_total || 0);
                    }
                    const ajouts = parseFloat(dossier.montant_ajouts || 0);
                    const totalTTC = montantTTC + ajouts;
                    if (totalTTC <= 0) return 'Non défini';
                    const totalHT = totalTTC / 1.14975;
                    return totalHT.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' });
                  })()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        )}
        <Card className="col-span-1">
          <CardContent className="pt-3 pb-3">
            <div className="flex items-center gap-2">
              <User className="w-6 h-6 text-purple-600 shrink-0" />
              <div className="min-w-0">
                <p className="text-xs text-gray-500">Représentant</p>
                <p className="font-semibold text-sm truncate">{dossier.representant_nom || 'Non assigné'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="col-span-1">
          <CardContent className="pt-3 pb-3">
            <div className="flex items-center gap-2">
              <ClipboardList className="w-6 h-6 text-amber-600 shrink-0" />
              <div className="min-w-0">
                <p className="text-xs text-gray-500">Chargée</p>
                <p className="font-semibold text-sm truncate">{dossier.charge_projet_nom || 'Non assigné'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="col-span-2 md:col-span-1">
          <CardContent className="pt-3 pb-3">
            <div className="flex items-center gap-2">
              <User className="w-6 h-6 text-green-600 shrink-0" />
              <div className="min-w-0">
                <p className="text-xs text-gray-500">Chef d'équipe</p>
                <p className="font-semibold text-sm truncate">{dossier.chef_equipe_nom || 'Non assigné'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        {dossier.adresse_travaux && !hideAddress && (
          <Card className="col-span-2 md:col-span-5">
            <CardContent className="pt-3 pb-3">
              <div className="flex items-center gap-2 flex-wrap">
                <MapPin className="w-5 h-5 text-slate-400 shrink-0" />
                <span className="text-sm text-slate-700 font-medium">{dossier.adresse_travaux}, {dossier.ville_travaux} {dossier.code_postal_travaux}</span>
                <GoogleMapsBtn address={dossier.adresse_travaux} ville={dossier.ville_travaux} codePostal={dossier.code_postal_travaux} size="md" />
              </div>
            </CardContent>
          </Card>
        )}
        {hideAddress && (
          <Card className="col-span-2 md:col-span-5 border-dashed border-2 border-cyan-200 bg-cyan-50">
            <CardContent className="pt-3 pb-3">
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-cyan-400 shrink-0" />
                <span className="text-sm text-cyan-700 italic">Adresse masquée — visible lors du démarrage de la route de lavage</span>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Onglets */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="overflow-x-auto -mx-3 sm:mx-0 no-print">
          <TabsList className="flex w-max min-w-full sm:w-full scrollbar-none" style={{scrollbarWidth:'none'}}>
            {(!canSeeDossierTab || canSeeDossierTab('info')) && <TabsTrigger value="info" className="whitespace-nowrap sm:flex-1"><Building2 className="w-4 h-4 mr-1 sm:mr-2 hidden sm:block" />Info</TabsTrigger>}
            {(!canSeeDossierTab || canSeeDossierTab('calculatrice')) && <TabsTrigger value="calculatrice" className="whitespace-nowrap sm:flex-1"><Calculator className="w-4 h-4 mr-1 sm:mr-2 hidden sm:block" /><span className="hidden sm:inline">Calculatrice</span><span className="sm:hidden">Calc.</span></TabsTrigger>}
            {(!canSeeDossierTab || canSeeDossierTab('soumissions')) && <TabsTrigger value="soumissions" className="whitespace-nowrap sm:flex-1"><FileText className="w-4 h-4 mr-1 sm:mr-2 hidden sm:block" /><span className="hidden sm:inline">Soumissions</span><span className="sm:hidden">Soum.</span></TabsTrigger>}
            {(!canSeeDossierTab || canSeeDossierTab('bon_travail')) && (
              <TabsTrigger value="bon-travail" className="relative whitespace-nowrap sm:flex-1">
                <ClipboardList className="w-4 h-4 mr-1 sm:mr-2 hidden sm:block" /><span className="hidden sm:inline">Bon de Travail</span><span className="sm:hidden">Bon de travail</span>
                {activeBonsCount > 0 && (
                  <span className="ml-1.5 min-w-[18px] h-[18px] bg-blue-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 leading-none">
                    {activeBonsCount}
                  </span>
                )}
                {openIncidentsCount > 0 && (
                  <span className="ml-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 leading-none">
                    {openIncidentsCount}
                  </span>
                )}
              </TabsTrigger>
            )}
            {showPhotosTab && canSeeDossierTab('photos') && (
              <TabsTrigger value="photos" className="relative whitespace-nowrap sm:flex-1">
                <Camera className="w-4 h-4 mr-1 hidden sm:block" />Photos Marketing & Vidéos
                {nbPhotos > 0 && (
                  <span className="ml-1 bg-blue-600 text-white text-xs rounded-full px-1.5 py-0.5 leading-none">
                    {nbPhotos}
                  </span>
                )}
              </TabsTrigger>
            )}
            {(!canSeeDossierTab || canSeeDossierTab('factures')) && <TabsTrigger value="factures" className="whitespace-nowrap sm:flex-1"><DollarSign className="w-4 h-4 mr-1 hidden sm:block" />Factures</TabsTrigger>}
            {(!canSeeDossierTab || canSeeDossierTab('stats')) && <TabsTrigger value="statistiques" className="whitespace-nowrap sm:flex-1"><BarChart3 className="w-4 h-4 mr-1 hidden sm:block" />Stats</TabsTrigger>}
            {(!canSeeDossierTab || canSeeDossierTab('notes')) && <TabsTrigger value="notes" className="whitespace-nowrap sm:flex-1"><MessageSquare className="w-4 h-4 mr-1 hidden sm:block" />Notes</TabsTrigger>}
            {(!canSeeDossierTab || canSeeDossierTab('historique')) && <TabsTrigger value="historique" className="whitespace-nowrap sm:flex-1"><Clock className="w-4 h-4 mr-1 hidden sm:block" />Historique</TabsTrigger>}
          </TabsList>
        </div>

        <TabsContent value="info">
          <Card>
            <CardHeader><CardTitle>Informations du Dossier</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold mb-4">Détails</h3>
                  <dl className="space-y-3">
                    <div>
                      <dt className="text-sm text-gray-600">Type de contrat</dt>
                      <dd className="font-medium capitalize">{dossier.type_contrat === 'a_l_heure' ? 'À l\'heure' : dossier.type_contrat}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-600">Département</dt>
                      <dd className="font-medium">{dossier.departement}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-600">Date de création</dt>
                      <dd className="font-medium">{formatLocalDate(dossier.date_creation)}</dd>
                    </div>
                  </dl>
                </div>
                <div>
                  <h3 className="font-semibold mb-4">Adresse des Travaux</h3>
                  {hideAddress ? (
                    <p className="text-cyan-700 italic bg-cyan-50 border border-dashed border-cyan-200 rounded-md p-2">
                      Adresse masquée — visible lors du démarrage de la route de lavage
                    </p>
                  ) : (
                    <div className="flex items-start gap-2 flex-wrap">
                      <p className="text-gray-700">{dossier.adresse_travaux}<br />{dossier.ville_travaux}, {dossier.code_postal_travaux}</p>
                      <GoogleMapsBtn address={dossier.adresse_travaux} ville={dossier.ville_travaux} codePostal={dossier.code_postal_travaux} size="md" />
                    </div>
                  )}
                  <h3 className="font-semibold mt-6 mb-4">Assignations</h3>
                  <dl className="space-y-2">
                    <div>
                      <dt className="text-sm text-gray-600">Représentant</dt>
                      <dd className="font-medium">{dossier.representant_nom || 'Non assigné'}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-600">Chef d'équipe</dt>
                      <dd className="font-medium">{dossier.chef_equipe_nom || 'Non assigné'}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-600">Chargé de projet</dt>
                      <dd className="font-medium">{dossier.charge_projet_nom || 'Non assigné'}</dd>
                    </div>
                  </dl>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="soumissions">
          <OngletSoumissions dossier={dossier} onUpdate={fetchDossier} />
        </TabsContent>
        <TabsContent value="calculatrice">
          <Card>
            <CardContent className="pt-6">
              <OngletCalculatrice dossier={dossier} />
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="bon-travail">
          <OngletBonTravail dossier={dossier} onUpdate={fetchDossier} selectedBonId={selectedBonId} autoOpenCommande={autoOpenCommande} hideAddress={hideAddress} />
        </TabsContent>
        {showPhotosTab && canSeeDossierTab('photos') && (
          <TabsContent value="photos">
            <OngletPhotos dossier={dossier} onUpdate={fetchDossier} />
          </TabsContent>
        )}
        <TabsContent value="factures">
          <OngletFactures dossier={dossier} onUpdate={() => { fetchDossier(); fetchMontantsDossier(); }} />
        </TabsContent>
        <TabsContent value="statistiques">
          <OngletStatistiques dossier={dossier} />
        </TabsContent>
        <TabsContent value="notes">
          <OngletNotes dossier={dossier} onUpdate={fetchDossier} />
        </TabsContent>
        <TabsContent value="historique">
          <OngletHistorique dossier={dossier} />
        </TabsContent>
      </Tabs>

      {/* Modal Modifier Projet */}
      <Dialog open={showModifier} onOpenChange={setShowModifier}>
        <DialogContent className="max-w-2xl w-[95vw] max-h-[85vh] overflow-y-auto" onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Modifier le Projet</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nom du projet *</Label>
              <Input value={editForm.nom_dossier || ''} onChange={(e) => setEditForm({ ...editForm, nom_dossier: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Département</Label>
                <Select value={editForm.departement || 'PRQC'} onValueChange={(val) => setEditForm({ ...editForm, departement: val })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent position="popper"><SelectItem value="PRQC">PRQC</SelectItem><SelectItem value="LMDS">LMDS</SelectItem><SelectItem value="LOCATION">LOCATION</SelectItem></SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date de création</Label>
                <Input type="date" value={editForm.date_creation || ''} onChange={(e) => setEditForm({ ...editForm, date_creation: e.target.value })} data-testid="edit-date-creation" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type de contrat</Label>
                <Select value={editForm.type_contrat || 'peinture'} onValueChange={(val) => setEditForm({ ...editForm, type_contrat: val })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent position="popper"><SelectItem value="peinture">Peinture</SelectItem><SelectItem value="location">Location</SelectItem><SelectItem value="a_l_heure">À l'heure</SelectItem></SelectContent>
                </Select>
                {editForm.type_contrat === 'a_l_heure' && (
                  <p className="text-xs text-blue-600 mt-1">Catégorie stats "À l'heure" assignée automatiquement</p>
                )}
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="font-semibold mb-3">Adresse des travaux</h3>
              <div className="space-y-3">
                <div>
                  <Label>Adresse</Label>
                  <Input value={editForm.adresse_travaux || ''} onChange={(e) => setEditForm({ ...editForm, adresse_travaux: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Ville</Label>
                    <Input value={editForm.ville_travaux || ''} onChange={(e) => setEditForm({ ...editForm, ville_travaux: e.target.value })} />
                  </div>
                  <div>
                    <Label>Code postal</Label>
                    <Input value={editForm.code_postal_travaux || ''} onChange={(e) => setEditForm({ ...editForm, code_postal_travaux: e.target.value })} />
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="font-semibold mb-3">Assignations</h3>
              <div className="space-y-4">
                <div>
                  <Label>Représentant</Label>
                  <Select value={editForm.representant_id || 'aucun'} onValueChange={handleRepChange}>
                    <SelectTrigger><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                    <SelectContent position="popper">
                      <SelectItem value="aucun">Aucun</SelectItem>
                      {representants.length > 0 ? representants.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>{emp.prenom} {emp.nom}</SelectItem>
                      )) : employees.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>{emp.prenom} {emp.nom}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Chef d'équipe</Label>
                  <Select value={editForm.chef_equipe_id || 'aucun'} onValueChange={handleChefChange}>
                    <SelectTrigger><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                    <SelectContent position="popper">
                      <SelectItem value="aucun">Aucun</SelectItem>
                      {chefsEquipe.length > 0 ? chefsEquipe.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>{emp.prenom} {emp.nom}</SelectItem>
                      )) : employees.filter(e => e.type_employe === 'terrain').map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>{emp.prenom} {emp.nom}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Chargé de projet</Label>
                  <Select value={editForm.charge_projet_id || 'aucun'} onValueChange={handleChargeChange}>
                    <SelectTrigger><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                    <SelectContent position="popper">
                      <SelectItem value="aucun">Aucun</SelectItem>
                      {chargesProjets.length > 0 ? chargesProjets.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>{emp.prenom} {emp.nom}</SelectItem>
                      )) : employees.filter(e => e.type_employe === 'admin').map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>{emp.prenom} {emp.nom}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Classification des travaux */}
            <div className="space-y-3 pt-2 border-t">
              <p className="text-sm font-semibold text-gray-700">Classification des travaux</p>
              <div>
                <Label className="text-sm">Type de travaux</Label>
                <div className="flex gap-2 mt-1.5">
                  {[
                    { value: 'interieur', label: '🏠 Intérieur' },
                    { value: 'exterieur', label: '🏡 Extérieur' },
                    { value: 'mixte', label: '🏠🏡 Mixte' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setEditForm({ ...editForm, type_travaux: opt.value })}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                        editForm.type_travaux === opt.value
                          ? 'bg-emerald-600 text-white border-emerald-600 font-semibold'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-emerald-400'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-sm">Catégorie de travaux</Label>
                <input
                  className="mt-1 flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  placeholder="Ex: Patio, Plafonds, Façade..."
                  value={editForm.categorie_travaux || ''}
                  onChange={(e) => setEditForm({ ...editForm, categorie_travaux: e.target.value })}
                  list="edit-categorie-suggestions"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowModifier(false)}>Annuler</Button>
              <Button onClick={handleSaveModifier} className="bg-blue-600 hover:bg-blue-700">Enregistrer</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Décharge Légale */}
      {showDechargeDialog && (
        <DechargeDialog
          dossier={dossier}
          onClose={() => setShowDechargeDialog(false)}
          onSent={() => { setShowDechargeDialog(false); fetchDossier(); }}
        />
      )}

      {/* Dialog Finaliser — demande les dates de production */}
      <Dialog open={showFinaliserDialog} onOpenChange={setShowFinaliserDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
              Finaliser le dossier
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-600">
              Indiquez la periode pendant laquelle le contrat a ete realise. Le montant HT sera reparti au prorata sur cette periode pour le calcul de la production.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Date debut production</Label>
                <Input
                  type="date"
                  value={finaliserDates.debut}
                  onChange={e => setFinaliserDates(d => ({ ...d, debut: e.target.value }))}
                  data-testid="finaliser-date-debut"
                />
              </div>
              <div>
                <Label className="text-xs">Date fin production</Label>
                <Input
                  type="date"
                  value={finaliserDates.fin}
                  onChange={e => setFinaliserDates(d => ({ ...d, fin: e.target.value }))}
                  data-testid="finaliser-date-fin"
                />
              </div>
            </div>
            {dossier?.heures_manuelles?.length > 0 ? (
              <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 rounded-lg p-3 border border-emerald-200 text-sm">
                <CheckCircle className="w-4 h-4 flex-shrink-0" />
                {dossier.heures_manuelles.length} entree(s) d'heures manuelles trouvees
              </div>
            ) : (
              <div className="text-amber-700 bg-amber-50 rounded-lg p-3 border border-amber-200 text-sm">
                Les dates de production sont facultatives. Si aucun Bon de Travail n'existe, les dates servent a repartir le montant dans le calcul de production.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFinaliserDialog(false)}>Annuler</Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={finaliserLoading}
              data-testid="confirm-finaliser-btn"
              onClick={async () => {
                setFinaliserLoading(true);
                try {
                  const res = await axios.patch(`${API_URL}/api/crm/dossiers/${dossier.id}/marquer-finalise`, {
                    date_debut_production: finaliserDates.debut || null,
                    date_fin_production: finaliserDates.fin || null,
                  });
                  if (res.status !== 200) { throw new Error(res.data?.detail || 'Erreur'); }
                  const result = res.data;
                  if (result.balance_zero) {
                    toast.success('Dossier ferme ! (facture finale deja payee)');
                  } else {
                    toast.success('Dossier finalise !');
                  }
                  setShowFinaliserDialog(false);
                  fetchDossier();
                } catch (e) { toast.error(e.message || 'Erreur'); }
                setFinaliserLoading(false);
              }}
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              {finaliserLoading ? 'Finalisation...' : 'Confirmer la finalisation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🆕 2026-05-01 — Dialog "Marquer Inactif" */}
      <Dialog open={showInactifDialog} onOpenChange={setShowInactifDialog}>
        <DialogContent className="max-w-md" data-testid="dialog-marquer-inactif">
          <DialogHeader>
            <DialogTitle>Marquer ce dossier comme Inactif</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-600">
              Le dossier sera déplacé dans la catégorie <strong>Inactifs</strong>. Il sera
              masqué par défaut dans les listes et exclu des KPI. Tu pourras le réactiver
              à tout moment.
            </p>
            <div>
              <Label className="text-xs font-medium">Raison de l'abandon *</Label>
              <Select
                value={inactifForm.raison}
                onValueChange={(v) => setInactifForm({ raison: v, raison_autre: v === 'Autre' ? inactifForm.raison_autre : '' })}
              >
                <SelectTrigger className="mt-1" data-testid="select-raison-inactif">
                  <SelectValue placeholder="Choisir une raison..." />
                </SelectTrigger>
                <SelectContent>
                  {RAISONS_ABANDON.map(r => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {inactifForm.raison === 'Autre' && (
              <div>
                <Label className="text-xs font-medium">Précisez *</Label>
                <Input
                  value={inactifForm.raison_autre}
                  onChange={(e) => setInactifForm({ ...inactifForm, raison_autre: e.target.value })}
                  placeholder="Décrivez la raison..."
                  className="mt-1"
                  data-testid="input-raison-autre"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInactifDialog(false)} disabled={inactifLoading}>
              Annuler
            </Button>
            <Button
              disabled={
                inactifLoading ||
                !inactifForm.raison ||
                (inactifForm.raison === 'Autre' && !(inactifForm.raison_autre || '').trim())
              }
              onClick={async () => {
                setInactifLoading(true);
                try {
                  await axios.post(`${API_URL}/api/crm/dossiers/${dossier.id}/marquer-abandonne`, {
                    raison: inactifForm.raison,
                    raison_autre: inactifForm.raison === 'Autre' ? (inactifForm.raison_autre || '').trim() : null,
                  });
                  toast.success('Dossier marqué comme Inactif');
                  setShowInactifDialog(false);
                  // 🆕 2026-05-18 — Le vrai bug racine était un Pydantic 422 :
                  //   `raison_autre: str = None` n'acceptait pas null en Pydantic v2.
                  //   Fixé en backend (`Optional[str] = None`) + normalisation `detail`
                  //   dans l'intercepteur axios pour éviter le React error #31.
                  //   On peut maintenant re-fetch en toute sécurité.
                  navigate('/crm/dossiers');
                } catch (e) {
                  const msg = typeof e?.response?.data?.detail === 'string'
                    ? e.response.data.detail
                    : 'Erreur lors du marquage';
                  toast.error(msg);
                } finally {
                  setInactifLoading(false);
                }
              }}
              className="bg-slate-600 hover:bg-slate-700 text-white"
              data-testid="btn-confirmer-inactif"
            >
              {inactifLoading ? 'Enregistrement...' : 'Confirmer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🆕 2026-05-01 — Dialog "Réactiver" */}
      <Dialog open={showReactiverDialog} onOpenChange={setShowReactiverDialog}>
        <DialogContent className="max-w-md" data-testid="dialog-reactiver">
          <DialogHeader>
            <DialogTitle>Réactiver le dossier</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-600">
              Vers quel statut veux-tu réactiver ce dossier ?
            </p>
            <Select value={reactiverCible} onValueChange={setReactiverCible}>
              <SelectTrigger data-testid="select-cible-reactivation">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="prospect">Prospect</SelectItem>
                <SelectItem value="soum_a_faire">Soumission à faire</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReactiverDialog(false)} disabled={reactiverLoading}>
              Annuler
            </Button>
            <Button
              disabled={reactiverLoading}
              onClick={async () => {
                setReactiverLoading(true);
                try {
                  await axios.post(`${API_URL}/api/crm/dossiers/${dossier.id}/reactiver`, { vers_statut: reactiverCible });
                  toast.success('Dossier réactivé');
                  setShowReactiverDialog(false);
                  fetchDossier();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Erreur');
                } finally {
                  setReactiverLoading(false);
                }
              }}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="btn-confirmer-reactivation"
            >
              {reactiverLoading ? 'Réactivation...' : 'Réactiver'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DechargeDialog({ dossier, onClose, onSent }) {
  const [loading, setLoading] = useState(false);
  const [template, setTemplate] = useState(null);
  const [email, setEmail] = useState(dossier.client_email || '');
  const [preview, setPreview] = useState('');
  const [sent, setSent] = useState(dossier.decharge_legale_statut === 'signe' || dossier.decharge_legale_statut === 'envoye');

  useEffect(() => {
    axios.get(`${API_URL}/api/legal/decharge-template`).then(r => r.data)
      .then(d => {
        setTemplate(d.template);
        const vars = {
          client_nom: dossier.client_nom || '',
          adresse_travaux: dossier.adresse_travaux || '',
          ville_travaux: dossier.ville_travaux || '',
          montant: dossier.montant_soumission?.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' }) || '',
          date: formatLocalDate(new Date().toISOString()),
        };
        let content = d.template?.contenu || '';
        Object.entries(vars).forEach(([k, v]) => { content = content.replaceAll(`{${k}}`, v); });
        setPreview(content);
      })
      .catch(console.error);
  }, []);

  const sendDecharge = async () => {
    if (!email) { toast.error('Veuillez entrer un email'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/legal/envoyer-decharge/${dossier.id}`, 
        { email }  // 🚨 l'URL publique est construite côté serveur (url_app)
      );
      if (res.status !== 200) throw new Error();
      toast.success('Décharge envoyée au client!');
      setSent(true);
      if (onSent) onSent();
    } catch { toast.error("Erreur lors de l'envoi"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-purple-600" />
            Décharge Légale — {dossier.client_nom}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {sent && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700">
              ✓ Une décharge a déjà été envoyée pour ce projet ({dossier.decharge_legale_statut === 'signe' ? 'Signée' : 'Envoyée'})
            </div>
          )}
          <div>
            <Label>Email du client</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="client@exemple.com" data-testid="decharge-email-input" />
          </div>
          {preview && (
            <div>
              <Label>Aperçu du courriel</Label>
              <div className="mt-1 border rounded-lg p-4 bg-white text-sm max-h-60 overflow-y-auto prose prose-sm" dangerouslySetInnerHTML={{ __html: (preview || '').replace(/\n/g, '<br/>') }} />
            </div>
          )}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
            Le client recevra un email avec le document à signer. Vous serez notifié une fois la décharge signée. Pour modifier le modèle, rendez-vous dans Configuration → Légal.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={sendDecharge} disabled={loading} className="bg-purple-600 hover:bg-purple-700" data-testid="send-decharge-btn">
            <Shield className="w-4 h-4 mr-2" />
            {loading ? 'Envoi...' : 'Envoyer au client'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
